import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import type { WalletBalance } from '../../types/wallet'
import {
  MINTS,
  fetchJupPerpsPositions,
  jupPerpsIncrease,
  jupPerpsDecrease,
  type JupPerpsPosition,
  type JupPerpsTxResponse,
} from '../../api/trade'
import { deserializeV0 } from '../../lib/tx'
import { useAdapterSignAndSubmit } from '../../lib/walletAdapter'
import { findBalance, formatBalance, formatUsdShort } from '../../hooks/useWalletBalance'
import TokenIcon from '../common/TokenIcon'
import TradeResultBanner, { type TradeResult } from './TradeResultBanner'

const USDC_DECIMALS = 6
const DEFAULT_SLIPPAGE_BPS = 200
// SOL the wallet must hold for tx fees on Jupiter Perps open/close. The
// collateral is USDC, so SOL is purely for fees + any new USDC ATA the perps
// program might create on the keeper side.
const MIN_SOL_FOR_HEDGE = 0.01

function parseUsdc(raw: string): bigint | null {
  if (!raw || !/^[0-9]*\.?[0-9]*$/.test(raw)) return null
  const [whole, frac = ''] = raw.split('.')
  const padded = (frac + '000000').slice(0, USDC_DECIMALS)
  try { return BigInt((whole || '0') + padded) || null } catch { return null }
}

function fmtUsd(v: string | number | undefined): string {
  const n = typeof v === 'string' ? parseFloat(v) : (v ?? 0)
  if (!Number.isFinite(n)) return '—'
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })
}

interface Props {
  balances: WalletBalance[] | null
  defaultAmount?: string
  defaultSolAmount?: string
}

export default function HedgeForm({ balances, defaultAmount, defaultSolAmount }: Props) {
  const { portfolioId } = useAuth()
  const { signAndSubmit, publicKey, connected, connect, wallet, friendlyTxError } = useAdapterSignAndSubmit()

  const isSolMode = !!defaultSolAmount
  const [mode, setMode] = useState<'open' | 'close'>('open')
  const [asset] = useState<'SOL'>('SOL')
  const [collateral, setCollateral] = useState(defaultAmount ?? '')
  const [solInput, setSolInput] = useState(defaultSolAmount ?? '')
  const [leverage, setLeverage] = useState(2)
  const [positions, setPositions] = useState<JupPerpsPosition[]>([])
  const [selectedPos, setSelectedPos] = useState<JupPerpsPosition | null>(null)
  const [result, setResult] = useState<TradeResult>({ kind: 'idle' })

  const usdcWallet = findBalance(balances, MINTS.USDC)
  const shortPos = positions.find(p => p.side === 'short' && p.asset === 'SOL') ?? null

  const reload = useCallback(async () => {
    if (!portfolioId) return
    const list = await fetchJupPerpsPositions(portfolioId)
    setPositions(list)
    const s = list.find(p => p.side === 'short' && p.asset === 'SOL')
    if (s) setSelectedPos(s)
  }, [portfolioId])

  useEffect(() => { reload() }, [reload])

  // In SOL-input mode: recompute USDC collateral whenever solInput or leverage changes.
  // collateral = solInput * solPrice / leverage  (the amount needed to achieve target notional)
  useEffect(() => {
    if (!isSolMode) return
    const solAmt = parseFloat(solInput)
    const solPrice = parseFloat(findBalance(balances, MINTS.SOL)?.priceUsd ?? '0')
    if (solAmt > 0 && solPrice > 0 && leverage > 0) {
      setCollateral(((solAmt * solPrice) / leverage).toFixed(2))
    }
  }, [isSolMode, solInput, leverage, balances])

  const submitTx = async (response: JupPerpsTxResponse, pendingLabel: string) => {
    const b64 = response.transaction ?? response.serializedTransaction
    if (!b64) throw new Error('Backend did not return a transaction')
    setResult({ kind: 'pending', label: pendingLabel })
    const tx = deserializeV0(b64)
    return signAndSubmit(tx, { lastValidBlockHeight: response.lastValidBlockHeight })
  }

  const openHedge = async () => {
    const bn = parseUsdc(collateral)
    if (!bn || bn <= 0n) return

    // If the wallet-adapter hasn't auto-connected yet, trigger a connect as the
    // FIRST step of the click — then fall through to the normal build+sign flow.
    if (!connected || !publicKey) {
      setResult({ kind: 'pending', label: 'Connecting wallet…' })
      try {
        if (!wallet) throw new Error('No wallet selected — open Phantom/Solflare via the Connect Wallet button in the header, then retry.')
        await connect()
      } catch (e) {
        setResult({ kind: 'error', message: friendlyTxError(e) })
        return
      }
    }

    setResult({ kind: 'pending', label: 'Building hedge transaction…' })
    try {
      if (!publicKey) throw new Error('Wallet not connected')
      if (portfolioId && publicKey.toBase58() !== portfolioId) {
        throw new Error('Connected wallet does not match your session')
      }
      const response = await jupPerpsIncrease({
        asset,
        side: 'short',
        inputToken: MINTS.USDC,
        inputTokenAmount: bn.toString(),
        leverage,
        maxSlippageBps: DEFAULT_SLIPPAGE_BPS,
        owner: publicKey.toBase58(),
      })
      const submitted = await submitTx(response, 'Waiting for wallet signature…')
      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `Opened ${leverage}× SHORT on ${asset} with ${collateral} USDC collateral`,
      })
      setCollateral('')
      setTimeout(reload, 5000)
    } catch (e: any) {
      // Jupiter's tx-build endpoint has been returning 500 for any payload since
      // their last API migration (docs marked "work-in-progress"). Surface this
      // distinctly so users don't think their input is wrong.
      const msg = String(e?.message ?? e)
      const isUpstream500 = /500|internal_server_error|contact jupiter support/i.test(msg)
      setResult({
        kind: 'error',
        message: isUpstream500
          ? 'Jupiter\'s tx-builder API is currently returning 500 for all requests (their Perps API is migrating). Use the Jupiter Perps UI button above to open this short in a new tab with the same wallet.'
          : friendlyTxError(e),
      })
    }
  }

  const closeHedge = async () => {
    if (!selectedPos) return

    // Same connect-on-click as openHedge — keeps UX symmetrical.
    if (!connected || !publicKey) {
      setResult({ kind: 'pending', label: 'Connecting wallet…' })
      try {
        if (!wallet) throw new Error('No wallet selected — open Phantom/Solflare via the Connect Wallet button in the header, then retry.')
        await connect()
      } catch (e) {
        setResult({ kind: 'error', message: friendlyTxError(e) })
        return
      }
    }

    setResult({ kind: 'pending', label: 'Building close transaction…' })
    try {
      if (!publicKey) throw new Error('Wallet not connected')
      const response = await jupPerpsDecrease({
        positionPubkey: selectedPos.positionPubkey,
        owner: publicKey.toBase58(),
        entirePosition: true,
        receiveToken: MINTS.USDC,
        maxSlippageBps: DEFAULT_SLIPPAGE_BPS,
      })
      const submitted = await submitTx(response, 'Waiting for wallet signature…')
      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `Closed SHORT position on ${selectedPos.asset}`,
      })
      setTimeout(reload, 5000)
    } catch (e) {
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }

  const onMaxCollateral = () => {
    if (!usdcWallet) return
    // Collateral is USDC, not SOL — the previous −1 USDC cushion was
    // incoherent. Tx fees come from native SOL and are gated separately by
    // the SOL preflight banner below.
    setCollateral(parseFloat(usdcWallet.amount).toFixed(2))
  }

  // SOL preflight — opens AND closes still pay tx fees from native SOL.
  const walletSol     = findBalance(balances, MINTS.SOL)
  const solBalanceNum = parseFloat(walletSol?.amount ?? '0')
  const insufficientSolForFees = balances != null && solBalanceNum < MIN_SOL_FOR_HEDGE

  // Allow the button even when the adapter isn't connected yet — the click
  // handler starts with a connect attempt. Prevents the "label says 'Connect
  // & Short' but the button is disabled" dead-end.
  const canOpen  = !!parseUsdc(collateral) && !insufficientSolForFees
  const canClose = !!selectedPos && !insufficientSolForFees

  return (
    <div className="space-y-3">
      {/* Open short position */}
      <div className="flex gap-1 rounded-lg bg-surface-hover p-1">
        {(['open', 'close'] as const).map(m => (
          <button key={m}
            onClick={() => { setMode(m); setResult({ kind: 'idle' }) }}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold ${mode === m ? 'bg-accent-purple/25 text-ink' : 'text-ink-muted'}`}
          >{m === 'open' ? 'Open short' : 'Close short'}</button>
        ))}
      </div>

      {/* Existing position summary */}
      {shortPos && (
        <div className="rounded-lg border border-yellow-400/25 bg-yellow-400/5 p-3">
          <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-ink-muted">
            <span>Open short on {shortPos.asset}</span>
            <span className="text-amber-400">{parseFloat(shortPos.leverage || '0').toFixed(1)}× leverage</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <Stat label="Size"         value={fmtUsd(shortPos.sizeUsd)} />
            <Stat label="Collateral"   value={fmtUsd(shortPos.collateralUsd)} />
            <Stat
              label="PnL"
              value={fmtUsd(shortPos.pnlUsd)}
              valueClass={parseFloat(shortPos.pnlUsd) >= 0 ? 'text-accent-green' : 'text-accent-red'}
            />
            <Stat label="Entry price"  value={fmtUsd(shortPos.entryPrice)} />
            <Stat label="Mark price"   value={fmtUsd(shortPos.markPrice)} />
            <Stat label="Liq. price"   value={fmtUsd(shortPos.liquidationPrice)} valueClass="text-accent-red" />
          </div>
        </div>
      )}

      {mode === 'open' ? (
        <>
          {/* Asset-being-shorted header — shows SOL prominently so there's no
              ambiguity between the asset (SOL) and the collateral (USDC). */}
          <div className="rounded-lg border border-accent-red/30 bg-accent-red/5 p-3">
            <div className="flex items-center gap-2.5">
              <TokenIcon symbol="SOL" mint={MINTS.SOL} size={28} />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-ink-muted">You are shorting</div>
                <div className="flex items-center gap-1.5">
                  <span className="text-base font-bold text-ink">{asset}</span>
                  <span className="rounded-full bg-accent-red/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent-red">
                    short
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-ink-muted">via</div>
                <div className="text-xs font-semibold text-ink">Jupiter Perps</div>
              </div>
            </div>
            <p className="mt-2 text-[10px] leading-relaxed text-ink-muted">
              A short on SOL profits when SOL price falls, hedging your spot SOL exposure.
              Jupiter Perps requires USDC as collateral (margin); it does not short the USDC itself.
            </p>
          </div>

          {isSolMode ? (
            /* SOL-amount input: user specifies how many SOL to hedge; collateral is derived */
            <>
              {(() => {
                const solWallet = findBalance(balances, MINTS.SOL)
                return solWallet && (
                  <div className="flex items-center justify-between rounded-lg bg-surface-hover px-3 py-2 text-[11px]">
                    <div className="flex items-center gap-1.5 text-gray-500">
                      <TokenIcon symbol="SOL" mint={MINTS.SOL} size={14} />
                      Wallet SOL
                    </div>
                    <div className="text-ink">
                      {formatBalance(solWallet.amount, 4)} SOL
                      <span className="ml-1 text-gray-500">· {formatUsdShort(solWallet.usdValue)}</span>
                    </div>
                  </div>
                )
              })()}
              <div className="rounded-lg border border-fringe bg-surface-hover p-3">
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500">
                    SOL to hedge
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <TokenIcon symbol="SOL" mint={MINTS.SOL} size={18} />
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.0"
                    value={solInput}
                    onChange={e => setSolInput(e.target.value)}
                    className="flex-1 bg-transparent text-lg font-semibold text-ink outline-none"
                  />
                </div>
              </div>
              {collateral && (
                <div className="rounded-lg bg-surface-hover px-3 py-2 text-[11px] text-gray-500">
                  USDC collateral needed: <span className="text-ink font-semibold">{fmtUsd(collateral)}</span>
                  {usdcWallet && (
                    <span className={parseFloat(collateral) > parseFloat(usdcWallet.amount) ? ' text-amber-400' : ''}>
                      {' '}(wallet: {formatBalance(usdcWallet.amount, 2)} USDC)
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            /* USDC-direct input — default mode used outside the wizard */
            <>
              {usdcWallet && (
                <div className="flex items-center justify-between rounded-lg bg-surface-hover px-3 py-2 text-[11px]">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <TokenIcon symbol="USDC" mint={MINTS.USDC} size={14} />
                    Wallet USDC (used as margin)
                  </div>
                  <div className="text-ink">
                    {formatBalance(usdcWallet.amount, 2)} USDC
                    <span className="ml-1 text-gray-500">· {formatUsdShort(usdcWallet.usdValue)}</span>
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-fringe bg-surface-hover p-3">
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500">
                    USDC margin (collateral)
                  </label>
                  {usdcWallet && (
                    <button type="button" onClick={onMaxCollateral} className="text-[10px] text-accent-purple hover:underline">
                      MAX {formatBalance(usdcWallet.amount, 2)}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <TokenIcon symbol="USDC" mint={MINTS.USDC} size={18} />
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.0"
                    value={collateral}
                    onChange={e => setCollateral(e.target.value)}
                    className="flex-1 bg-transparent text-lg font-semibold text-ink outline-none"
                  />
                </div>
              </div>
            </>
          )}

          <div className="rounded-lg bg-surface-hover p-3">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-wider text-gray-500">Leverage</label>
              <span className="text-sm font-semibold text-ink">{leverage.toFixed(1)}×</span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              step={0.5}
              value={leverage}
              onChange={e => setLeverage(Number(e.target.value))}
              className="w-full accent-accent-purple"
            />
            <div className="mt-1 flex gap-1">
              {[1, 2, 3, 5, 10].map(v => (
                <button key={v}
                  onClick={() => setLeverage(v)}
                  className={`flex-1 rounded-md px-2 py-1 text-[11px] ${leverage === v ? 'bg-accent-purple/25 text-ink' : 'text-ink-muted hover:bg-surface-hover'}`}
                >{v}×</button>
              ))}
            </div>
          </div>

          <div className="space-y-1 rounded-lg bg-surface-hover p-3 text-[11px]">
            {isSolMode
              ? <Row label={`${asset} short notional`} value={solInput ? `${parseFloat(solInput).toFixed(4)} SOL` : '—'} />
              : <Row label={`${asset} short notional`} value={collateral ? fmtUsd(parseFloat(collateral) * leverage) : '—'} />
            }
            <Row label="Asset being shorted" value={asset} />
            <Row label="Margin token" value="USDC" />
            <Row label="Slippage" value={(DEFAULT_SLIPPAGE_BPS / 100).toFixed(2) + '%'} />
          </div>

          {insufficientSolForFees && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed">
              Wallet has only {solBalanceNum.toFixed(4)} SOL — Jupiter Perps tx fees and any
              new USDC ATA rent are paid from native SOL. Top up at least {MIN_SOL_FOR_HEDGE} SOL and retry.
            </div>
          )}

          <a
            href="https://jup.ag/perps/SOL"
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-fringe-strong bg-surface-hover py-2 text-xs font-semibold text-ink-muted hover:text-ink transition-colors"
          >
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-3M9 2h5m0 0v5m0-5L8 9"/>
            </svg>
            Open in Jupiter Perps UI
            <span className="ml-0.5 text-[10px] text-gray-500">(same wallet)</span>
          </a>

          <button
            type="button"
            disabled={!canOpen || result.kind === 'pending'}
            onClick={openHedge}
            className="w-full rounded-lg bg-accent-purple py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-accent-purple/40"
          >
            {result.kind === 'pending'
              ? 'Working…'
              : insufficientSolForFees ? 'Top up SOL to short'
              : connected ? `Sign & Short ${leverage}× ${asset}` : `Connect & Short ${asset}`}
          </button>
        </>
      ) : (
        <>
          {positions.length === 0 && (
            <div className="rounded-lg bg-surface-hover px-3 py-4 text-center text-[11px] text-gray-500">
              No open perpetual positions.
            </div>
          )}

          {positions.map(p => (
            <button
              key={p.positionPubkey}
              onClick={() => setSelectedPos(p)}
              className={`w-full rounded-lg border px-3 py-2 text-left text-xs transition-colors
                ${selectedPos?.positionPubkey === p.positionPubkey
                  ? 'border-accent-purple bg-accent-purple/15'
                  : 'border-fringe bg-surface-hover hover:bg-surface-hover'}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-ink">
                  {p.side.toUpperCase()} {p.asset} · {parseFloat(p.leverage || '0').toFixed(1)}×
                </span>
                <span className={parseFloat(p.pnlUsd) >= 0 ? 'text-accent-green' : 'text-accent-red'}>
                  {fmtUsd(p.pnlUsd)}
                </span>
              </div>
              <div className="text-[10px] text-gray-500">
                Size {fmtUsd(p.sizeUsd)} · Collateral {fmtUsd(p.collateralUsd)}
              </div>
            </button>
          ))}

          {insufficientSolForFees && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed">
              Wallet has only {solBalanceNum.toFixed(4)} SOL — closing the perp still pays
              tx fees from native SOL. Top up at least {MIN_SOL_FOR_HEDGE} SOL and retry.
            </div>
          )}

          <button
            type="button"
            disabled={!canClose || result.kind === 'pending'}
            onClick={closeHedge}
            className="w-full rounded-lg border border-accent-red/50 bg-accent-red/15 py-2.5 text-sm font-semibold text-accent-red disabled:cursor-not-allowed"
          >
            {result.kind === 'pending'
              ? 'Working…'
              : insufficientSolForFees ? 'Top up SOL to close'
              : connected ? 'Sign & Close position' : 'Connect & Close'}
          </button>
        </>
      )}

      <TradeResultBanner result={result} onDismiss={() => setResult({ kind: 'idle' })} />

      <p className="text-[10px] text-gray-500">
        Jupiter Perps uses a request-fulfillment model — the transaction creates a request that a keeper settles at the next oracle tick (~2 s). Your position appears after settlement.
      </p>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-gray-500">
      <span>{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  )
}

function Stat({ label, value, valueClass = 'text-ink' }: { label: string; value: string; valueClass?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-xs font-semibold ${valueClass}`}>{value}</div>
    </div>
  )
}
